import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  PencilSquareIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { downloadExamDocument, getExamWordPreview, getExams, startExamWord } from "../../../services/Exams/ExamsService";
import { getApiErrorCode, getApiErrorMessage } from "../../../utils/apiError";
import { Spinner, focusRing } from "../TestBuilder/ui";
import useWordJob from "./useWordJob";
import {
  HEADING_WARNINGS,
  footerPhrase,
  groupWarnings,
  isLiveJob,
  jobErrorText,
  keyRefusalText,
  languageName,
  partLabel,
  printedHeading,
  sizeText,
  stageLabel,
  warningCount,
  warningText,
} from "./wordText";

// The exam page's dialog look (ViewExams): 44px buttons on phones, two at most in a row.
const buttonClass = `inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 text-sm font-medium transition duration-200 sm:h-10 ${focusRing}`;
const primaryClass = `${buttonClass} bg-blue-600 text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:bg-blue-600 aria-busy:cursor-wait`;
const secondaryClass = `${buttonClass} bg-white text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900`;
const linkClass = `inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-sm font-medium text-blue-700 transition duration-200 hover:bg-blue-50 hover:text-blue-800 max-sm:min-h-[2.75rem] ${focusRing}`;
const dotClass = "mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400";

// Shown at first in a test's list of questions with warnings; the rest behind "show all".
const SHOWN = 6;

const hasText = (value) => typeof value === "string" && value.trim() !== "";

// What a warning names besides its text: the picture's address or the formula's TeX.
const warningExtra = (item) => item.src || item.tex || "";

// A warning's line: its place in the question (an option, the passage) and what happens.
function WarningLine({ item }) {
  const [t] = useTranslation();
  const part = partLabel(t, item.part);
  const extra = warningExtra(item);
  return (
    <span className="block min-w-0 text-slate-700">
      {part && `${part}: `}
      {warningText(t, item)}
      {extra && (
        <span className="block truncate font-mono text-xs text-slate-500" title={extra}>
          {extra}
        </span>
      )}
    </span>
  );
}

// The questions of a test with warnings, each question once with its warnings under its number.
function QuestionWarnings({ groups }) {
  const [t] = useTranslation();
  const [all, setAll] = useState(false);
  const shown = all ? groups : groups.slice(0, SHOWN);
  return (
    <>
      <ul className="mt-2 space-y-1.5 text-sm">
        {shown.map(({ number, items }) => (
          <li key={number} className="flex min-w-0 gap-2">
            <span className={dotClass} aria-hidden="true" />
            <span className="min-w-0 flex-1">
              <span className="font-medium text-slate-900">{t("exam_bank.word.where.question", { number })}</span>
              {items.map((item, index) => (
                <WarningLine key={index} item={item} />
              ))}
            </span>
          </li>
        ))}
      </ul>
      {groups.length > SHOWN && (
        <button type="button" aria-expanded={all} onClick={() => setAll((value) => !value)} className={`mt-1 ${linkClass}`}>
          {all ? t("exam_bank.word.show_less") : t("exam_bank.word.show_all", { count: groups.length })}
        </button>
      )}
    </>
  );
}

/**
 * A test as the Word prints it: its number, its heading in capitals (the test's name under it when it differs), its
 * questions and points, and what to check in it. A heading that is a mere name (or none) links to the test's page,
 * where its own page heading is set (`?edit=heading` opens the test's settings at that field).
 */
function SectionItem({ section, index, language, group, examUuid, onLeave }) {
  const [t] = useTranslation();
  const heading = printedHeading(section.heading, language);
  const name = (section.name || "").trim();
  const nameDiffers = name && printedHeading(name, language) !== heading;
  const own = group?.own || [];
  const questions = group?.questions || [];
  const headingTest = own.find((item) => HEADING_WARNINGS.includes(item.code))?.test || section.test;
  const asksHeading = own.some((item) => HEADING_WARNINGS.includes(item.code)) && !!headingTest;
  return (
    <li className="rounded-xl border border-slate-200 p-3" data-testid="word-section">
      <div className="flex items-start gap-3">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-xs font-semibold tabular-nums text-blue-700 ring-1 ring-inset ring-blue-600/10"
          aria-hidden="true"
        >
          {index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            <p className="min-w-0 break-words text-sm font-semibold tracking-wide text-slate-900">{heading || "—"}</p>
            <p className="shrink-0 text-xs tabular-nums text-slate-500">
              {t("exam_bank.word.questions", { count: section.questions || 0 })} · {t("exam_bank.word.points", { count: section.points || 0 })}
            </p>
          </div>
          {nameDiffers && <p className="mt-0.5 break-words text-xs text-slate-500">{t("exam_bank.word.test_name", { name })}</p>}
        </div>
      </div>
      {/* What to check: under the heading from sm up, the card's whole width on phones. */}
      {(own.length > 0 || questions.length > 0) && (
        <div className="sm:pl-10">
          {own.length > 0 && (
            <ul className="mt-2 space-y-1.5 text-sm">
              {own.map((item, i) => (
                <li key={i} className="flex min-w-0 gap-2">
                  <span className={dotClass} aria-hidden="true" />
                  <WarningLine item={item} />
                </li>
              ))}
            </ul>
          )}
          {asksHeading && (
            <Link
              to={`/test/${headingTest}?edit=heading`}
              state={{ from: `/exams_list/${examUuid}` }}
              onClick={onLeave}
              className={`ml-2.5 mt-1 ${linkClass}`}
              data-testid="word-set-heading"
            >
              <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
              {t("exam_bank.word.set_heading")}
              <span className="sr-only">: {name || heading}</span>
            </Link>
          )}
          {questions.length > 0 && <QuestionWarnings groups={questions} />}
        </div>
      )}
    </li>
  );
}

/**
 * What the Word will print: its language (a calm note when none is chosen: it prints Russian), the variant code and
 * the programme of its footer, then the tests in their order, the tests left out (no questions) and the warnings
 * that are not about one test. `onOpenSettings(field)` opens the exam's settings (null: the exam is read-only).
 */
function PrintPreview({ print, warnings, examUuid, onOpenSettings, onLeave }) {
  const [t] = useTranslation();
  const titleId = useId();
  const sections = Array.isArray(print.sections) ? print.sections : [];
  const skipped = Array.isArray(print.skipped) ? print.skipped : [];
  const grouped = groupWarnings(warnings);
  const count = warningCount(warnings);
  const language = print.language;
  return (
    <div className="space-y-5">
      <section aria-labelledby={`${titleId}-print`}>
        <h3 id={`${titleId}-print`} className="text-sm font-semibold text-slate-900">
          {t("exam_bank.word.printed_title")}
        </h3>
        <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm" data-testid="word-print-settings">
          <dt className="text-slate-500">{t("exam_bank.word.language")}</dt>
          <dd className="min-w-0 text-slate-900">
            {print.language_set === false ? (
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="word-language-unset">
                <span className="inline-flex items-start gap-1.5 text-amber-800">
                  <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                  {t("exam_bank.word.language_unset")}
                </span>
                {onOpenSettings && (
                  <button type="button" onClick={() => onOpenSettings("language")} className={linkClass} data-testid="word-choose-language">
                    {t("exam_bank.word.choose_language")}
                  </button>
                )}
              </span>
            ) : (
              <span className="font-medium">{languageName(t, language)}</span>
            )}
          </dd>
          <dt className="text-slate-500">{t("exam_bank.word.code")}</dt>
          <dd className="min-w-0 text-slate-900">
            <span className="font-medium">{hasText(print.code) ? print.code : "—"}</span>
            <span className="text-slate-500">
              {" · "}
              {hasText(print.code)
                ? t("exam_bank.word.code_footer", { phrase: footerPhrase(language, print.code) })
                : t("exam_bank.word.code_none_hint")}
            </span>
          </dd>
          <dt className="text-slate-500">{t("exam_bank.word.program")}</dt>
          <dd className="min-w-0 text-slate-900">
            <span className="font-medium">{hasText(print.program) ? print.program : "—"}</span>
            {hasText(print.program) && <span className="text-slate-500"> · {t("exam_bank.word.program_hint")}</span>}
          </dd>
        </dl>
        <p className="mt-2 text-xs text-slate-500">
          {t("exam_bank.word.settings_from")}{" "}
          {onOpenSettings && (
            <button type="button" onClick={() => onOpenSettings()} className={`${linkClass} -my-0.5 text-xs`} data-testid="word-open-settings">
              {t("exam_bank.word.settings_link")}
            </button>
          )}
        </p>
      </section>

      <section aria-labelledby={`${titleId}-tests`}>
        <h3 id={`${titleId}-tests`} className="text-sm font-semibold text-slate-900">
          {t("exam_bank.word.tests_title", { count: sections.length })}
        </h3>
        {/* Many warnings: said once above the tests (a few show at a glance under their tests). */}
        {count > 2 && (
          <p className="mt-1 flex items-start gap-1.5 text-sm text-amber-800">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            {t("exam_bank.word.warnings_summary", { count })}
          </p>
        )}
        {sections.length > 0 && (
          <ol className="mt-2 space-y-2">
            {sections.map((section, index) => (
              <SectionItem
                key={`${section.test}-${index}`}
                section={section}
                index={index}
                language={language}
                group={grouped.sections[index]}
                examUuid={examUuid}
                onLeave={onLeave}
              />
            ))}
          </ol>
        )}
        {skipped.length > 0 && (
          <p className="mt-2 text-sm text-slate-500" data-testid="word-skipped">
            {t("exam_bank.word.skipped", {
              names: skipped.map((item) => (item.name || "").trim() || t("exam_bank.word.untitled_test")).join(", "),
            })}
          </p>
        )}
        {grouped.general.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-medium text-slate-900">{t("exam_bank.word.general_warnings")}</h4>
            <ul className="mt-1.5 space-y-1.5 text-sm">
              {grouped.general.map((item, index) => (
                <li key={index} className="flex min-w-0 gap-2">
                  <span className={dotClass} aria-hidden="true" />
                  <WarningLine item={item} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}

// The exams of the user's other Word jobs (429 too_many_word_jobs: [{uuid, status, stage, exam}], `exam` its uuid),
// by name where the page can learn it; an exam it cannot read is left out.
async function jobExams(jobs, examUuid, examName) {
  const ids = [];
  (Array.isArray(jobs) ? jobs : []).forEach((job) => {
    const exam = job?.exam && typeof job.exam === "object" ? job.exam : { uuid: job?.exam };
    if (exam.uuid && !ids.some((item) => item.uuid === exam.uuid)) ids.push(exam);
  });
  const named = await Promise.all(
    ids.map(async (exam) => {
      if (exam.uuid === examUuid) return { uuid: exam.uuid, name: examName, current: true };
      if (hasText(exam.name)) return { uuid: exam.uuid, name: exam.name };
      try {
        const data = await getExams({ params: { uuid: exam.uuid } });
        return hasText(data?.name) ? { uuid: exam.uuid, name: data.name } : null;
      } catch (err) {
        return null;
      }
    })
  );
  return named.filter(Boolean);
}

/**
 * «Word құжаты»: what the exam's Word prints (GET word/), and making it. «Word жасау» asks for it (POST word/):
 * 201, the key and the student copy are ready (a download button for each, nothing downloads by itself); 202, its
 * job is followed (a polite line says its stage) until it ends with the two files or fails (why, in words). A job
 * under way when the dialog opens (GET word/'s job, else `rememberedJob`, the one under way when it last closed) is
 * followed at once. Closing the dialog stops following (it is mounted only while open); the job goes on.
 * The key is the user's only when they may read every question it marks in the question bank: when GET word/ (then
 * the POST) says answers_hidden, a calm line says before and while it is made that the key will not be given; the
 * export then ends with the student copy alone (document null, in the 201 or the job) and the line says so.
 *
 * Props: examUuid, examName; onClose(); onJobChange(job) (the page remembers a live one); onFilesChanged() (an
 * export ended with files: the page reads its documents again); onOpenSettings(field) opens the exam's settings at
 * "language" (or at the top), null when the exam is read-only.
 */
export default function ExamWordDialog({ examUuid, examName, rememberedJob, onClose, onJobChange, onFilesChanged, onOpenSettings }) {
  const [t, i18nInstance] = useTranslation();
  const language = i18nInstance.resolvedLanguage || i18nInstance.language;
  const uid = useId();
  const nothingId = `${uid}-nothing`;
  const keyNoteId = `${uid}-key`;
  const [preview, setPreview] = useState(null);
  // The key of the Word made now would not be the user's (answers_hidden of GET word/, then of the POST's answer).
  const [keyHidden, setKeyHidden] = useState(false);
  // loading | ready | failed | forbidden (403/404: nothing can be made here)
  const [loadState, setLoadState] = useState("loading");
  const [loadMessage, setLoadMessage] = useState("");
  const [starting, setStarting] = useState(false);
  // A refusal of «Word жасау»: {tone: "warning" | "error" | "info", text, kind, exams}.
  const [notice, setNotice] = useState(null);
  // The job followed is another one of this exam, started before its latest changes (409 word_job_running).
  const [otherJob, setOtherJob] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  // A refused download: {text, held} (held: a key not given to the user, said calmly).
  const [downloadError, setDownloadError] = useState(null);
  const { job, problem, follow, resume, clear } = useWordJob();

  const primaryRef = useRef(null);
  const firstFileRef = useRef(null);
  const panelRef = useRef(null);
  const postRef = useRef(null);
  const aliveRef = useRef(true);
  const rememberedRef = useRef(rememberedJob);
  const onJobChangeRef = useRef(onJobChange);
  onJobChangeRef.current = onJobChange;
  const onFilesChangedRef = useRef(onFilesChanged);
  onFilesChangedRef.current = onFilesChanged;

  const load = useCallback(
    async (signal) => {
      setLoadState("loading");
      try {
        const data = await getExamWordPreview(examUuid, { signal });
        setPreview({ print: data?.print || null, warnings: Array.isArray(data?.warnings) ? data.warnings : [] });
        setKeyHidden(data?.answers_hidden === true);
        setLoadState("ready");
        if (data?.job?.uuid) follow(data.job);
        else if (rememberedRef.current) follow(rememberedRef.current);
        rememberedRef.current = null;
      } catch (err) {
        if (signal?.aborted) return;
        const status = err?.response?.status;
        if (status === 403 || (status === 404 && getApiErrorCode(err) === "not_found")) {
          setLoadState("forbidden");
          setLoadMessage(status === 403 ? t("errors.permission_denied") : t("exam_taking.exam_not_found"));
          return;
        }
        // Not read (the network, a server without the preview): the Word can still be asked for.
        setLoadState("failed");
        setLoadMessage(err?.response ? t("exam_bank.word.load_error") : t("exam_bank.word.network"));
        if (rememberedRef.current) follow(rememberedRef.current);
        rememberedRef.current = null;
      }
    },
    [examUuid, follow, t]
  );

  useEffect(() => {
    aliveRef.current = true;
    const controller = new AbortController();
    load(controller.signal);
    return () => {
      aliveRef.current = false;
      controller.abort();
      postRef.current?.abort();
    };
  }, [load]);

  // The page learns of the job (it remembers one under way when the dialog closes).
  useEffect(() => {
    onJobChangeRef.current?.(job);
  }, [job]);

  // An export that ended with its files: the page's list shows them. Said once per job.
  const filesForRef = useRef(null);
  useEffect(() => {
    if (job?.status === "succeeded" && filesForRef.current !== job.uuid) {
      filesForRef.current = job.uuid;
      onFilesChangedRef.current?.();
    }
  }, [job]);

  // Another version's job ended: it is not what this dialog makes; the dialog is back to making one.
  useEffect(() => {
    if (otherJob && job && !isLiveJob(job)) {
      setOtherJob(false);
      clear();
      setNotice({ tone: "info", text: t("exam_bank.word.other_job_ended"), kind: "other_ended" });
    }
  }, [otherJob, job, clear, t]);

  const succeeded = job?.status === "succeeded" && !otherJob;
  const failed = job?.status === "failed" && !otherJob;
  // A job the server no longer knows (404) is not under way, whatever it last said.
  const live = isLiveJob(job) && problem !== "gone";
  const watching = live && !problem;
  // The job's own print once there is one (what its files hold); another version's job says nothing of this one.
  const shown = job?.print && !otherJob ? { print: job.print, warnings: Array.isArray(job.warnings) ? job.warnings : [] } : preview;
  const nothing = !!shown?.print && (shown.print.sections || []).length === 0 && !succeeded;
  const busy = starting || live;
  const blocked = loadState === "loading" || loadState === "forbidden" || busy || nothing;
  // Made without the key, which is not the user's: the student copy alone is offered (the 201's or the job's document
  // null). A server before the student copy gives the key alone, so a document missing beside it is the key held back.
  const keyWithheld = succeeded && !job.document && (!!job.student_document || job.answers_hidden === true);
  // Until then, the key the Word now made would hold back is said beforehand: not for an empty Word (none is made),
  // nor beside a job that failed or is gone (its failure is said alone; the line is back with the next one).
  const keyAhead = !succeeded && !failed && problem !== "gone" && keyHidden && !nothing;

  // The files once ready: the primary button goes, the first download takes the focus where it was.
  useEffect(() => {
    if (!succeeded) return;
    const active = document.activeElement;
    if (!active || active === document.body || !panelRef.current?.contains(active)) firstFileRef.current?.focus();
  }, [succeeded]);

  const startFailed = (err) => {
    const status = err?.response?.status;
    const data = err?.response?.data && typeof err.response.data === "object" ? err.response.data : {};
    const code = getApiErrorCode(err);
    if (!err?.response) {
      setNotice({ tone: "error", text: t("exam_bank.word.network") });
    } else if (code === "word_job_running" && data.job?.uuid) {
      setOtherJob(true);
      follow(data.job);
      setNotice({ tone: "warning", text: t("exam_bank.word.other_job"), kind: "other" });
    } else if (code === "too_many_word_jobs") {
      setNotice({ tone: "warning", text: t("errors.too_many_word_jobs"), kind: "too_many", exams: [] });
      jobExams(data.jobs, examUuid, examName).then((exams) => {
        if (!aliveRef.current) return;
        setNotice((prev) => (prev?.kind === "too_many" ? { ...prev, exams } : prev));
      });
    } else if (code === "nothing_to_print") {
      if (data.print) setPreview({ print: data.print, warnings: Array.isArray(data.warnings) ? data.warnings : [] });
      setLoadState("ready");
      if (!data.print) setNotice({ tone: "warning", text: t("errors.nothing_to_print") });
    } else if (data.job?.uuid && data.job.status === "failed") {
      // Failed while the server waited: its error_code says why.
      follow(data.job);
    } else if (status === 404) {
      setNotice({ tone: "error", text: t("exam_taking.exam_not_found") });
    } else if (code === "internal_error") {
      setNotice({ tone: "error", text: t("exam_bank.word.job_error.internal_error") });
    } else {
      setNotice({ tone: "error", text: getApiErrorMessage(t, err, "exam_bank.view.word_error") });
    }
  };

  const make = async () => {
    if (blocked) return;
    const controller = new AbortController();
    postRef.current = controller;
    setNotice(null);
    setDownloadError(null);
    setOtherJob(false);
    clear();
    setStarting(true);
    try {
      const body = await startExamWord(examUuid, { signal: controller.signal });
      if (controller.signal.aborted) return;
      // Whether its key is the user's: the 201's for the files made, the 202's for those to come (its job says so
      // only once they are made), kept for the end.
      if (typeof body?.answers_hidden === "boolean") setKeyHidden(body.answers_hidden);
      const made = body?.document || body?.student_document;
      if (made) {
        // 201: the files are there, read no more: the key and the student copy, or the student copy alone when the
        // key is not the user's (document null); a server before the student copy gives the one document.
        follow({
          ...(body.job || {}),
          uuid: body.job?.uuid || made.id,
          status: "succeeded",
          document: body.document || null,
          student_document: body.student_document || null,
          answers_hidden: body.answers_hidden === true,
          print: body.print,
          warnings: body.warnings,
        });
      } else if (body?.job?.uuid) {
        // 202: the job outlasted the server's wait.
        follow({ ...body.job, print: body.print, warnings: body.warnings });
      } else {
        setNotice({ tone: "error", text: t("exam_bank.view.word_error") });
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      startFailed(err);
    } finally {
      if (postRef.current === controller) {
        postRef.current = null;
        if (aliveRef.current) setStarting(false);
      }
    }
  };

  const download = async (doc) => {
    setDownloadingId(doc.id);
    setDownloadError(null);
    try {
      await downloadExamDocument(doc);
    } catch (err) {
      if (!aliveRef.current) return;
      // A key held back since it was offered (403 answers_hidden) says why, as the documents list does.
      const held = err?.response ? keyRefusalText(t, err) : null;
      setDownloadError({
        text: !err?.response
          ? t("exam_bank.word.network")
          : held || getApiErrorMessage(t, err, "exam_bank.documents.download_error"),
        held: !!held,
      });
    } finally {
      if (aliveRef.current) setDownloadingId(null);
    }
  };

  const files = succeeded
    ? [
        job.document && {
          doc: job.document,
          kind: "key",
          label: job.student_document ? t("exam_bank.word.download_key") : t("buttons.download"),
        },
        job.student_document && { doc: job.student_document, kind: "student", label: t("exam_bank.word.download_student") },
      ].filter(Boolean)
    : [];
  const primaryLabel = busy
    ? t("exam_bank.view.word_generating")
    : failed || problem === "gone"
      ? t("exam_bank.word.remake")
      : t("exam_bank.view.word_generate");
  const showPrimary = !succeeded;
  // The strip above the footer takes room only when it has something to say (its live region stays in place, without
  // a gap of its own while it is empty).
  const statusEmpty = !(starting || watching) && !(nothing && !busy) && !succeeded;
  // Nothing shown above that empty region: the block after it (a lost or failed job) opens the strip, without a gap.
  const leadClass = statusEmpty && !notice && !keyAhead ? "!mt-0" : "";
  const hasResult = !!notice || starting || watching || (nothing && !busy) || succeeded || failed || !!problem || keyAhead;
  const describedBy = [nothing && !busy ? nothingId : null, keyAhead ? keyNoteId : null].filter(Boolean).join(" ");

  return (
    <Dialog open onClose={onClose} initialFocus={primaryRef} className="relative z-50">
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
        <Dialog.Panel
          ref={panelRef}
          className="flex max-h-[calc(100dvh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/5 motion-safe:animate-fade-in"
          data-testid="exam-word-dialog"
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 sm:px-6">
            <div className="min-w-0">
              <Dialog.Title as="h2" className="text-lg font-semibold text-slate-900">
                {t("exam_bank.word.title")}
              </Dialog.Title>
              {examName && <p className="mt-0.5 break-words text-sm text-slate-500">{examName}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("buttons.close")}
              className={`-mr-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-slate-400 transition duration-200 hover:bg-slate-100 hover:text-slate-700 sm:h-9 sm:w-9 ${focusRing}`}
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          {/* What will be printed: scrolls inside the dialog (a focusable region, for the keyboard). */}
          <div
            role="region"
            aria-label={t("exam_bank.word.printed_title")}
            tabIndex={0}
            className={`min-h-0 flex-1 overflow-y-auto px-5 py-4 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600 sm:px-6`}
            data-testid="exam-word-body"
          >
            {loadState === "loading" && !shown && (
              <p className="flex items-center gap-2 py-6 text-sm text-slate-500">
                <Spinner />
                {t("exam_bank.word.loading")}
              </p>
            )}
            {(loadState === "failed" || loadState === "forbidden") && !shown && (
              <p className={`flex flex-wrap items-center gap-x-2 gap-y-1 py-2 text-sm ${loadState === "forbidden" ? "text-red-700" : "text-slate-600"}`}>
                {loadState === "forbidden" ? (
                  <XCircleIcon className="h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                ) : (
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                )}
                <span className="min-w-0">{loadMessage}</span>
                {loadState === "failed" && (
                  <button type="button" onClick={() => load()} className={linkClass}>
                    <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                    {t("exam_bank.word.retry")}
                  </button>
                )}
              </p>
            )}
            {shown?.print && (
              <PrintPreview
                print={shown.print}
                warnings={shown.warnings}
                examUuid={examUuid}
                onOpenSettings={onOpenSettings}
                onLeave={onClose}
              />
            )}
          </div>

          {/* The export: its progress (said politely), its files, why it failed or was refused. */}
          <div
            className={hasResult ? "space-y-3 border-t border-slate-200 px-5 py-4 sm:px-6" : ""}
            data-testid="exam-word-result"
          >
            {notice && (
              <div
                role={notice.tone === "error" ? "alert" : "status"}
                className={`rounded-xl border px-3 py-2.5 text-sm ${
                  notice.tone === "error"
                    ? "border-red-200 bg-red-50 text-red-800"
                    : notice.tone === "warning"
                      ? "border-amber-200 bg-amber-50 text-amber-900"
                      : "border-slate-200 bg-slate-50 text-slate-700"
                }`}
                data-testid="exam-word-notice"
              >
                <p className="flex items-start gap-2">
                  {notice.tone === "error" ? (
                    <XCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                  ) : notice.tone === "warning" ? (
                    <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                  ) : (
                    <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
                  )}
                  <span className="min-w-0">{notice.text}</span>
                </p>
                {notice.kind === "too_many" && notice.exams?.length > 0 && (
                  <div className="ml-6 mt-1.5">
                    <p>{t("exam_bank.word.too_many_list")}</p>
                    <ul className="mt-1 list-disc space-y-0.5 pl-5">
                      {notice.exams.map((exam) => (
                        <li key={exam.uuid}>
                          {exam.current ? (
                            exam.name
                          ) : (
                            <Link to={`/exams_list/${exam.uuid}`} onClick={onClose} className={`${linkClass} -mx-1`}>
                              {exam.name}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* The key the Word will not give the user, said before it is made (beside the button that makes it). */}
            {keyAhead && (
              <p id={keyNoteId} className="flex items-start gap-2 text-sm text-amber-900" data-testid="exam-word-key-hidden">
                <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                <span className="min-w-0">{t("exam_bank.word.key_hidden_ahead")}</span>
              </p>
            )}

            <div role="status" className={`text-sm ${statusEmpty ? "!mt-0" : ""}`} data-testid="exam-word-status">
              {(starting || watching) && (
                <div className="flex items-start gap-3" data-testid="exam-word-progress">
                  <Spinner className="mt-0.5 h-5 w-5" />
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900">
                      {t("exam_bank.word.running_title")}
                      {live && <span className="font-normal text-slate-600"> · {stageLabel(t, job.stage)}</span>}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">{t("exam_bank.word.running_hint")}</p>
                  </div>
                </div>
              )}
              {nothing && !busy && (
                <p id={nothingId} className="flex items-start gap-2 text-amber-900">
                  <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                  {t("errors.nothing_to_print")}
                </p>
              )}
              {succeeded && (
                <p className="flex items-center gap-2 font-medium text-slate-900">
                  <CheckCircleIcon className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
                  {files.length > 1 ? t("exam_bank.word.ready_title") : t("exam_bank.word.ready_one")}
                </p>
              )}
              {/* Made without the key (not the user's): said with the result, the student copy below. */}
              {keyWithheld && (
                <p className="mt-1.5 flex items-start gap-2 text-amber-900" data-testid="exam-word-key-withheld">
                  <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                  <span className="min-w-0">{t("exam_bank.word.key_hidden_done")}</span>
                </p>
              )}
            </div>

            {live && problem && problem !== "gone" && (
              <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-amber-900 ${leadClass}`} role="status">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  {problem === "stalled" ? t("exam_bank.word.stalled") : t("exam_bank.word.poll_error")}{" "}
                  <span className="text-amber-800">{t("exam_bank.word.last_stage", { stage: stageLabel(t, job.stage) })}</span>
                </span>
                <button type="button" onClick={resume} className={linkClass}>
                  <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                  {t("exam_bank.word.check_again")}
                </button>
              </div>
            )}
            {problem === "gone" && (
              <p role="alert" className={`flex items-start gap-2 text-sm text-red-800 ${leadClass}`}>
                <XCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                {t("exam_bank.word.job_gone")}
              </p>
            )}

            {succeeded && files.length > 0 && (
              <div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  {files.map(({ doc, kind, label }, index) => {
                    const size = sizeText(doc.file_size, language);
                    const downloading = downloadingId === doc.id;
                    return (
                      <button
                        key={doc.id}
                        ref={index === 0 ? firstFileRef : undefined}
                        type="button"
                        onClick={() => !downloading && download(doc)}
                        aria-disabled={downloading || undefined}
                        aria-busy={downloading || undefined}
                        aria-label={t("exam_bank.word.download_aria", { name: label, size: size || "—" })}
                        title={doc.filename}
                        className={`${secondaryClass} justify-between gap-3 aria-disabled:cursor-wait sm:flex-1`}
                        data-testid={kind === "key" ? "exam-word-download-key" : "exam-word-download-student"}
                      >
                        <span className="inline-flex min-w-0 items-center gap-2">
                          {downloading ? <Spinner /> : <ArrowDownTrayIcon className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />}
                          <span className="truncate">{label}</span>
                        </span>
                        {size && <span className="shrink-0 text-xs font-normal tabular-nums text-slate-500">{size}</span>}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-slate-500">{t("exam_bank.word.ready_hint")}</p>
                {downloadError && (
                  <p
                    role="alert"
                    className={`mt-2 flex items-start gap-2 text-sm ${downloadError.held ? "text-amber-900" : "text-red-800"}`}
                    data-testid="exam-word-download-error"
                  >
                    {downloadError.held ? (
                      <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                    ) : (
                      <XCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                    )}
                    <span className="min-w-0">{downloadError.text}</span>
                  </p>
                )}
              </div>
            )}

            {failed && (
              <div
                role="alert"
                className={`rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800 ${leadClass}`}
                data-testid="exam-word-failed"
              >
                <p className="flex items-start gap-2 font-medium">
                  <XCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                  {t("exam_bank.word.failed_title")}
                </p>
                <p className="ml-6 mt-0.5">{jobErrorText(t, job)}</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:px-6">
            <button type="button" onClick={onClose} className={secondaryClass}>
              {t("buttons.close")}
            </button>
            {showPrimary && (
              <button
                ref={primaryRef}
                type="button"
                onClick={make}
                aria-disabled={blocked || undefined}
                aria-busy={busy || undefined}
                aria-describedby={describedBy || undefined}
                className={`${primaryClass} max-sm:flex-1`}
                data-testid="exam-word-make"
              >
                {busy && <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />}
                {primaryLabel}
              </button>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
